// Client state: one store, updated by REST responses and gateway dispatches.
import { useSyncExternalStore } from "react";
import type { Me, Member, Message, PresenceStatus, ReadyServer, Channel, Role, User, DmChannel, Relationship, ReadState, UserSettings, NotifyLevel, VoiceState, CallState } from "@majlis/protocol/types";
import { VoiceEngine } from "./voice.ts";
import { get, post, patch, upload as uploadFile, ApiError } from "./api.ts";
import { navigate } from "./router.ts";
import { GatewayClient, type GatewayStatus } from "./gateway.ts";

export interface PendingMessage {
  nonce: string; channelId: string; content: string; replyToId?: string; attachmentIds: string[];
  attachments: { name: string; type: string }[]; status: "sending" | "failed"; error?: string; createdAt: string;
}
export interface ChannelMessages { list: Message[]; hasMore: boolean; loading: boolean; loaded: boolean }
export interface ServerState extends ReadyServer { members: Record<string, Member> | null }

export interface State {
  phase: "boot" | "anon" | "ready";
  me: Me | null;
  servers: Record<string, ServerState>;
  serverOrder: string[];
  presences: Record<string, PresenceStatus>;
  users: Record<string, User>;
  messages: Record<string, ChannelMessages>;
  pending: Record<string, PendingMessage[]>;
  typing: Record<string, Record<string, number>>;
  conn: GatewayStatus;
  myStatus: "online" | "idle" | "dnd" | "invisible";
  /** Increments on every fresh READY; views that cache per-channel data re-fetch when it changes. */
  epoch: number;
  dms: Record<string, DmChannel>;
  relationships: Record<string, Relationship>;
  readStates: Record<string, ReadState>;
  settings: UserSettings;
  /** Channel currently on screen (used for unread and notification decisions). */
  activeChannelId: string | null;
  toast: { id: number; text: string } | null;
  /** channelId → userId → state, for every voice room / call we can see. */
  voiceStates: Record<string, Record<string, VoiceState>>;
  calls: Record<string, CallState>;
}

let state: State = {
  phase: "boot", me: null, servers: {}, serverOrder: [], presences: {}, users: {}, messages: {}, pending: {}, typing: {},
  conn: "connecting", myStatus: "online", toast: null, epoch: 0,
  dms: {}, relationships: {}, readStates: {}, activeChannelId: null, voiceStates: {}, calls: {},
  settings: { dmPolicy: "server_members", desktopNotifications: false, serverNotify: {}, mutedChannels: [] },
};
const subs = new Set<() => void>();
export const getState = () => state;
function set(patch: Partial<State> | ((s: State) => Partial<State>)) {
  state = { ...state, ...(typeof patch === "function" ? patch(state) : patch) };
  subs.forEach((f) => f());
}
export function useStore<T>(sel: (s: State) => T): T {
  return useSyncExternalStore((cb) => { subs.add(cb); return () => subs.delete(cb); }, () => sel(state));
}

let toastN = 0;
export function toast(text: string) {
  const id = ++toastN;
  set({ toast: { id, text } });
  setTimeout(() => { if (state.toast?.id === id) set({ toast: null }); }, 3500);
}
export const errText = (e: unknown) => (e instanceof ApiError ? e.message : (e as Error)?.message ?? "Error");

// ---------- server helpers ----------
const sortChannels = (cs: Channel[]) => [...cs].sort((a, b) => a.position - b.position || (a.id < b.id ? -1 : 1));
export function upsertServer(s: ReadyServer) {
  set((st) => ({
    servers: { ...st.servers, [s.id]: { ...s, channels: sortChannels(s.channels), members: st.servers[s.id]?.members ?? null } },
    serverOrder: st.serverOrder.includes(s.id) ? st.serverOrder : [...st.serverOrder, s.id],
  }));
}
function removeServer(id: string) {
  set((st) => { const servers = { ...st.servers }; delete servers[id]; return { servers, serverOrder: st.serverOrder.filter((x) => x !== id) }; });
}
function patchServer(id: string, fn: (s: ServerState) => Partial<ServerState>) {
  set((st) => { const s = st.servers[id]; if (!s) return {}; return { servers: { ...st.servers, [id]: { ...s, ...fn(s) } } }; });
}
export async function refreshServer(id: string) {
  try { upsertServer(await get<ReadyServer>(`/servers/${id}`)); } catch (e) { if (e instanceof ApiError && e.status === 404) removeServer(id); }
}
export async function loadMembers(serverId: string, force = false) {
  if (!force && state.servers[serverId]?.members) return;
  const list = await get<Member[]>(`/servers/${serverId}/members`);
  const members: Record<string, Member> = {};
  const users = { ...state.users }; const presences = { ...state.presences };
  for (const m of list) { members[m.user.id] = m; users[m.user.id] = m.user; if (!(m.user.id in presences)) presences[m.user.id] = m.status; }
  set({ users, presences });
  patchServer(serverId, () => ({ members }));
}

// ---------- messages ----------
function cm(channelId: string): ChannelMessages { return state.messages[channelId] ?? { list: [], hasMore: true, loading: false, loaded: false }; }
function setCm(channelId: string, patch: Partial<ChannelMessages>) { set((st) => ({ messages: { ...st.messages, [channelId]: { ...cm(channelId), ...patch } } })); }
const sortById = (l: Message[]) => l.sort((a, b) => (BigInt(a.id) < BigInt(b.id) ? -1 : 1));
function mergeById(a: Message[], b: Message[]): Message[] {
  const map = new Map<string, Message>(); for (const m of [...a, ...b]) map.set(m.id, m);
  return sortById([...map.values()]);
}
function insertMessage(m: Message) {
  const c = state.messages[m.channelId];
  set((st) => ({ users: { ...st.users, [m.author.id]: m.author } }));
  if (m.nonce) set((st) => ({ pending: { ...st.pending, [m.channelId]: (st.pending[m.channelId] ?? []).filter((p) => p.nonce !== m.nonce) } }));
  if (!c || (!c.loaded && !c.loading)) return; // never opened: history will be fetched when it is
  if (c.list.some((x) => x.id === m.id)) return;
  // While history is loading we still keep live events; openChannel merges them with the fetched page.
  setCm(m.channelId, { list: sortById([...c.list, m]) });
  if (state.typing[m.channelId]?.[m.author.id]) stopTyping(m.channelId, m.author.id);
}
function updateMessage(m: Message) {
  const c = state.messages[m.channelId];
  if (!c) return;
  setCm(m.channelId, { list: c.list.map((x) => (x.id === m.id ? { ...m, reactions: x.reactions.length && m.reactions.every((r) => !r.me) ? mergeReactions(x.reactions, m.reactions) : m.reactions } : x)) });
}
const mergeReactions = (mine: Message["reactions"], server: Message["reactions"]) =>
  server.map((r) => ({ ...r, me: mine.find((x) => x.emoji === r.emoji)?.me ?? false }));

export async function openChannel(channelId: string) {
  const c = cm(channelId);
  if (c.loaded || c.loading) return;
  setCm(channelId, { loading: true });
  try {
    const list = await get<Message[]>(`/channels/${channelId}/messages?limit=50`);
    setCm(channelId, { list: mergeById(list.reverse(), cm(channelId).list), hasMore: list.length === 50, loading: false, loaded: true });
  } catch (e) { setCm(channelId, { loading: false }); throw e; }
}
export async function loadOlder(channelId: string) {
  const c = cm(channelId);
  if (!c.loaded || c.loading || !c.hasMore || !c.list.length) return;
  setCm(channelId, { loading: true });
  try {
    const older = await get<Message[]>(`/channels/${channelId}/messages?limit=50&before=${c.list[0]!.id}`);
    const cur = cm(channelId);
    setCm(channelId, { list: [...older.reverse(), ...cur.list], hasMore: older.length === 50, loading: false });
  } catch (e) { setCm(channelId, { loading: false }); throw e; }
}

const newNonce = () => `${Date.now().toString(36)}-${crypto.getRandomValues(new Uint32Array(2)).join("")}`;

export function sendMessage(channelId: string, content: string, opts: { replyToId?: string; attachmentIds?: string[]; attachments?: { name: string; type: string }[] } = {}) {
  const p: PendingMessage = {
    nonce: newNonce(), channelId, content, replyToId: opts.replyToId, attachmentIds: opts.attachmentIds ?? [],
    attachments: opts.attachments ?? [], status: "sending", createdAt: new Date().toISOString(),
  };
  set((st) => ({ pending: { ...st.pending, [channelId]: [...(st.pending[channelId] ?? []), p] } }));
  void deliver(p);
}
async function deliver(p: PendingMessage) {
  try {
    const m = await post<Message>(`/channels/${p.channelId}/messages`, { content: p.content, nonce: p.nonce, replyToId: p.replyToId, attachmentIds: p.attachmentIds });
    insertMessage(m); bumpChannel(m);
  } catch (e) {
    set((st) => ({ pending: { ...st.pending, [p.channelId]: (st.pending[p.channelId] ?? []).map((x) => (x.nonce === p.nonce ? { ...x, status: "failed", error: errText(e) } : x)) } }));
  }
}
export function retryPending(channelId: string, nonce: string) {
  const p = state.pending[channelId]?.find((x) => x.nonce === nonce);
  if (!p) return;
  const again = { ...p, status: "sending" as const, error: undefined };
  set((st) => ({ pending: { ...st.pending, [channelId]: (st.pending[channelId] ?? []).map((x) => (x.nonce === nonce ? again : x)) } }));
  void deliver(again); // same nonce: the server deduplicates if the first attempt actually landed
}
export function discardPending(channelId: string, nonce: string) {
  set((st) => ({ pending: { ...st.pending, [channelId]: (st.pending[channelId] ?? []).filter((x) => x.nonce !== nonce) } }));
}
export const upload = uploadFile;

// ---------- typing ----------
function stopTyping(channelId: string, userId: string) {
  set((st) => { const c = { ...(st.typing[channelId] ?? {}) }; delete c[userId]; return { typing: { ...st.typing, [channelId]: c } }; });
}
setInterval(() => {
  const now = Date.now(); let changed = false; const typing: State["typing"] = {};
  for (const [cid, users] of Object.entries(state.typing)) {
    typing[cid] = {};
    for (const [uid, exp] of Object.entries(users)) { if (exp > now) typing[cid]![uid] = exp; else changed = true; }
  }
  if (changed) set({ typing });
}, 1000);

// ---------- unread state ----------
const gt = (a: string | null | undefined, b: string | null | undefined) => !!a && (!b || BigInt(a) > BigInt(b));
export function isUnread(st: State, channelId: string, lastMessageId: string | null): boolean {
  return gt(lastMessageId, st.readStates[channelId]?.lastReadId);
}
export const mentionsIn = (st: State, channelId: string) => st.readStates[channelId]?.mentionCount ?? 0;
export const isMuted = (st: State, channelId: string) => st.settings.mutedChannels.includes(channelId);
export const notifyLevel = (st: State, serverId: string): NotifyLevel => st.settings.serverNotify[serverId] ?? "mentions";

/** Server badge: total mentions, plus whether any visible, unmuted text channel is unread. */
export function serverBadge(st: State, serverId: string): { mentions: number; unread: boolean } {
  const s = st.servers[serverId]; if (!s) return { mentions: 0, unread: false };
  const level = notifyLevel(st, serverId);
  let mentions = 0, unread = false;
  for (const c of s.channels) {
    if (c.kind !== "text" || isMuted(st, c.id)) continue;
    if (level !== "none") mentions += mentionsIn(st, c.id);
    if (level === "all" && isUnread(st, c.id, c.lastMessageId)) unread = true;
    if (level === "mentions" && isUnread(st, c.id, c.lastMessageId)) unread = true;
  }
  return { mentions, unread };
}
export function homeBadge(st: State): number {
  let n = Object.values(st.relationships).filter((r) => r.type === "incoming").length;
  for (const dm of Object.values(st.dms)) if (!isMuted(st, dm.id)) n += mentionsIn(st, dm.id);
  return n;
}
export function totalBadge(st: State): number {
  return homeBadge(st) + st.serverOrder.reduce((n, id) => n + serverBadge(st, id).mentions, 0);
}

function setReadState(r: ReadState) { set((st) => ({ readStates: { ...st.readStates, [r.channelId]: r } })); }
const acking = new Map<string, string>();
/** Marks a channel read up to `messageId` (deduplicated; local state updates immediately). */
export function ack(channelId: string, messageId: string) {
  const cur = state.readStates[channelId];
  if (cur && !gt(messageId, cur.lastReadId) && !cur.mentionCount) return;
  if (acking.get(channelId) === messageId) return;
  acking.set(channelId, messageId);
  setReadState({ channelId, lastReadId: messageId, mentionCount: 0 });
  post(`/channels/${channelId}/ack`, { messageId }).catch(() => {}).finally(() => { if (acking.get(channelId) === messageId) acking.delete(channelId); });
}
export function setActiveChannel(id: string | null) { if (state.activeChannelId !== id) set({ activeChannelId: id }); }

function bumpChannel(m: Message) {
  if (m.serverId) patchServer(m.serverId, (s) => ({ channels: s.channels.map((c) => (c.id === m.channelId ? { ...c, lastMessageId: m.id } : c)) }));
  else if (state.dms[m.channelId]) set((st) => ({ dms: { ...st.dms, [m.channelId]: { ...st.dms[m.channelId]!, lastMessageId: m.id } } }));
  const me = state.me?.id;
  if (m.author.id === me) { setReadState({ channelId: m.channelId, lastReadId: m.id, mentionCount: 0 }); return; }
  const viewing = state.activeChannelId === m.channelId && document.visibilityState === "visible" && document.hasFocus();
  const pinged = !m.serverId || m.mentions.includes(me ?? "") || m.mentionEveryone;
  if (!viewing && pinged) {
    const cur = state.readStates[m.channelId];
    setReadState({ channelId: m.channelId, lastReadId: cur?.lastReadId ?? "0", mentionCount: (cur?.mentionCount ?? 0) + 1 });
  }
  maybeNotify(m, pinged, viewing);
}

// ---------- desktop notifications ----------
export function notificationsSupported() { return typeof Notification !== "undefined"; }
function maybeNotify(m: Message, pinged: boolean, viewing: boolean) {
  const st = state;
  if (viewing || !st.settings.desktopNotifications || !notificationsSupported() || Notification.permission !== "granted") return;
  if (st.myStatus === "dnd" || isMuted(st, m.channelId)) return;
  if (st.relationships[m.author.id]?.type === "blocked") return;
  let title: string, url: string;
  if (m.serverId) {
    const level = notifyLevel(st, m.serverId);
    if (level === "none" || (level === "mentions" && !pinged)) return;
    const s = st.servers[m.serverId]; const c = s?.channels.find((x) => x.id === m.channelId);
    title = `${m.author.displayName || m.author.username} (#${c?.name ?? ""}, ${s?.name ?? ""})`;
    url = `/channels/${m.serverId}/${m.channelId}`;
  } else {
    const dm = st.dms[m.channelId];
    title = dm?.kind === "group_dm" ? `${m.author.displayName || m.author.username} · ${dmName(dm)}` : (m.author.displayName || m.author.username);
    url = `/channels/@me/${m.channelId}`;
  }
  try {
    const n = new Notification(title, { body: m.content.slice(0, 180) || "📎", tag: m.channelId, icon: "/favicon.svg" });
    n.onclick = () => { window.focus(); navigate(url); n.close(); };
  } catch { /* some browsers only allow notifications from a service worker */ }
}

// ---------- DMs & friends ----------
export const dmName = (dm: DmChannel) => dm.name || dm.recipients.map((u) => u.displayName || u.username).join(", ") || "—";
function upsertDm(dm: DmChannel) {
  set((st) => ({ dms: { ...st.dms, [dm.id]: dm }, users: Object.assign({ ...st.users }, ...dm.recipients.map((u) => ({ [u.id]: u }))) }));
}
export function dmOrder(st: State): DmChannel[] {
  return Object.values(st.dms).sort((a, b) => { const x = BigInt(a.lastMessageId ?? a.id), y = BigInt(b.lastMessageId ?? b.id); return x > y ? -1 : x < y ? 1 : 0; });
}
export async function openDm(userId: string) {
  const dm = await post<DmChannel>("/users/@me/channels", { recipientId: userId });
  upsertDm(dm);
  navigate(`/channels/@me/${dm.id}`);
}
export async function updateSettings(p: Record<string, unknown>) {
  const s = await patch<UserSettings>("/users/@me/settings", p);
  set({ settings: s });
  return s;
}

// ---------- gateway ----------
let gw: GatewayClient | null = null;
export function setMyStatus(s: State["myStatus"]) {
  set({ myStatus: s });
  try { localStorage.setItem("majlis-status", s); } catch { /* ignore */ }
  gw?.setPresence(s);
}

function indexVoice(list: VoiceState[]) {
  const out: Record<string, Record<string, VoiceState>> = {};
  for (const v of list) (out[v.channelId] ??= {})[v.userId] = v;
  return out;
}

function onDispatch(t: string, d: any) {
  switch (t) {
    case "VOICE_STATE_UPDATE": {
      set((st) => {
        const vs = { ...st.voiceStates };
        for (const [cid, room] of Object.entries(vs)) if (room[d.userId] && (cid !== d.channelId || d.left)) { const r = { ...room }; delete r[d.userId]; if (Object.keys(r).length) vs[cid] = r; else delete vs[cid]; }
        if (!d.left) vs[d.channelId] = { ...(vs[d.channelId] ?? {}), [d.userId]: d };
        return { voiceStates: vs };
      });
      voice.onVoiceState(d);
      return;
    }
    case "VOICE_SIGNAL": void voice.onSignal(d.from, d.data); return;
    case "CALL_UPDATE": set((st) => ({ calls: { ...st.calls, [d.channelId]: d } })); return;
    case "CALL_DELETE": set((st) => { const c = { ...st.calls }; delete c[d.channelId]; return { calls: c }; }); return;
    case "READY": {
      const servers: Record<string, ServerState> = {};
      for (const s of d.servers as ReadyServer[]) servers[s.id] = { ...s, channels: sortChannels(s.channels), members: null };
      // A fresh READY means we may have missed events: drop message caches so channels refetch.
      const dms: Record<string, DmChannel> = {}; const users = { ...state.users };
      for (const dm of d.dms as DmChannel[]) { dms[dm.id] = dm; for (const u of dm.recipients) users[u.id] = u; }
      const relationships: Record<string, Relationship> = {};
      for (const r of d.relationships as Relationship[]) { relationships[r.user.id] = r; users[r.user.id] = r.user; }
      const readStates: Record<string, ReadState> = {};
      for (const r of d.readStates as ReadState[]) readStates[r.channelId] = r;
      voice.setMe(d.user.id);
      const voiceStates = indexVoice(d.voiceStates ?? []);
      const calls: Record<string, CallState> = {}; for (const c of (d.calls ?? []) as CallState[]) calls[c.channelId] = c;
      set({ voiceStates, calls });
      voice.onReady(d.voiceStates ?? []);
      set((st) => ({ phase: "ready", me: d.user, servers, serverOrder: d.servers.map((s: ReadyServer) => s.id), presences: { ...st.presences, ...d.presences },
        messages: {}, epoch: st.epoch + 1, dms, relationships, readStates, users, settings: d.settings }));
      break;
    }
    case "SERVER_CREATE": upsertServer(d); break;
    case "SERVER_UPDATE": patchServer(d.id, () => ({ name: d.name, description: d.description, ownerId: d.ownerId,
      ...(d.iconUrl !== undefined ? { iconUrl: d.iconUrl, iconAnimatedUrl: d.iconAnimatedUrl ?? null, bannerUrl: d.bannerUrl ?? null } : {}) })); break;
    case "SERVER_DELETE": removeServer(d.id); break;
    case "PERMISSIONS_CHANGED": void refreshServer(d.serverId); break;
    case "CHANNEL_CREATE": case "CHANNEL_UPDATE": patchServer(d.serverId, (s) => ({ channels: sortChannels([...s.channels.filter((c) => c.id !== d.id), d]) })); break;
    case "CHANNEL_DELETE": patchServer(d.serverId, (s) => ({ channels: s.channels.filter((c) => c.id !== d.id) })); break;
    case "ROLE_CREATE": case "ROLE_UPDATE": patchServer(d.serverId, (s) => ({ roles: [...s.roles.filter((r) => r.id !== d.id), d as Role].sort((a, b) => b.position - a.position) })); break;
    case "ROLE_DELETE": patchServer(d.serverId, (s) => ({ roles: s.roles.filter((r) => r.id !== d.id) })); break;
    case "MEMBER_ADD": case "MEMBER_UPDATE":
      set((st) => ({ users: { ...st.users, [d.member.user.id]: d.member.user } }));
      patchServer(d.serverId, (s) => ({ memberCount: s.memberCount + (t === "MEMBER_ADD" && !s.members?.[d.member.user.id] ? 1 : 0),
        members: s.members ? { ...s.members, [d.member.user.id]: d.member } : null,
        myRoleIds: d.member.user.id === state.me?.id ? d.member.roleIds : s.myRoleIds }));
      break;
    case "MEMBER_REMOVE": patchServer(d.serverId, (s) => { const m = { ...(s.members ?? {}) }; delete m[d.userId]; return { members: s.members ? m : null, memberCount: Math.max(0, s.memberCount - 1) }; }); break;
    case "PRESENCE_UPDATE": set((st) => ({ presences: { ...st.presences, [d.userId]: d.status } })); break;
    case "USER_UPDATE": {
      set((st) => ({ users: { ...st.users, [d.id]: d }, me: st.me && st.me.id === d.id ? { ...st.me, ...d } : st.me }));
      for (const [sid, s] of Object.entries(state.servers)) if (s.members?.[d.id]) patchServer(sid, (x) => ({ members: { ...x.members!, [d.id]: { ...x.members![d.id]!, user: d } } }));
      break;
    }
    case "MESSAGE_CREATE": insertMessage(d); bumpChannel(d); break;
    case "DM_CREATE": case "DM_UPDATE": upsertDm(d); break;
    case "DM_DELETE": set((st) => { const dms = { ...st.dms }; delete dms[d.id]; return { dms }; }); break;
    case "RELATIONSHIP_UPSERT": set((st) => ({ relationships: { ...st.relationships, [d.user.id]: d }, users: { ...st.users, [d.user.id]: d.user } })); break;
    case "RELATIONSHIP_REMOVE": set((st) => { const r = { ...st.relationships }; delete r[d.userId]; return { relationships: r }; }); break;
    case "READ_STATE_UPDATE": setReadState(d); break;
    case "SETTINGS_UPDATE": set({ settings: d }); break;
    case "MESSAGE_UPDATE": updateMessage(d); break;
    case "MESSAGE_DELETE": { const c = state.messages[d.channelId]; if (c) setCm(d.channelId, { list: c.list.filter((m) => m.id !== d.id) }); break; }
    case "REACTION_UPDATE": {
      const c = state.messages[d.channelId]; if (!c) break;
      const mine = d.userId === state.me?.id;
      setCm(d.channelId, { list: c.list.map((m) => {
        if (m.id !== d.messageId) return m;
        const has = m.reactions.find((r) => r.emoji === d.emoji);
        let reactions = has ? m.reactions.map((r) => (r.emoji === d.emoji ? { ...r, count: d.count, me: mine ? d.added : r.me } : r))
          : [...m.reactions, { emoji: d.emoji, count: d.count, me: mine && d.added }];
        reactions = reactions.filter((r) => r.count > 0);
        return { ...m, reactions };
      }) });
      break;
    }
    case "TYPING_START": set((st) => ({ typing: { ...st.typing, [d.channelId]: { ...(st.typing[d.channelId] ?? {}), [d.userId]: Date.now() + 9000 } } })); break;
  }
}

export async function boot() {
  try {
    const { user } = await get<{ user: Me | null }>("/auth/session");
    if (!user) { set({ phase: "anon", me: null }); return; }
    set({ me: user, phase: "boot" }); // loading screen until the gateway's READY arrives
    startGateway();
  } catch { set({ phase: "anon", me: null }); }
}
export function startGateway() {
  gw?.stop();
  try { const s = localStorage.getItem("majlis-status"); if (s === "idle" || s === "dnd" || s === "invisible") state.myStatus = s; } catch { /* ignore */ }
  gw = new GatewayClient({ dispatch: onDispatch, status: (conn) => set({ conn }), fatal: () => signedOut() });
  gw.onFrame = (op, d) => { if (op === "error") voice.onGatewayError(d); };
  gw.setPresence(state.myStatus);
  gw.start();
}
export function signedOut() {
  voice.leave();
  gw?.stop(); gw = null;
  set({ phase: "anon", me: null, servers: {}, serverOrder: [], messages: {}, pending: {}, typing: {}, presences: {}, users: {}, dms: {}, relationships: {}, readStates: {}, activeChannelId: null, voiceStates: {}, calls: {} });
}
export function setMe(me: Me) { set({ me }); }

// ---------- voice ----------
export const voice = new VoiceEngine((f) => gw?.send(f));
if (typeof window !== "undefined") (window as any).__majlisVoice = voice; // diagnostics + E2E (stats only; no secrets)
export function joinVoice(channelId: string) {
  const ch = Object.values(state.servers).flatMap((s) => s.channels).find((c) => c.id === channelId);
  void voice.join(channelId, ch?.serverId ?? null, ch?.bitrate ?? 64000, () => Object.values(state.voiceStates[channelId] ?? {}));
}
export const leaveVoice = () => voice.leave();
export function declineCall(channelId: string) { gw?.send({ op: "call_ring", d: { channelId, ring: false } }); }
/** Apply a channel we just created/edited via REST without waiting for the gateway echo (prevents redirect races). */
export function upsertChannel(c: Channel) { patchServer(c.serverId, (s) => ({ channels: sortChannels([...s.channels.filter((x) => x.id !== c.id), c]) })); }
